import { AuthMethod, LEGACY_WIDGET_AUTH_METHOD } from 'config/constants';
import { createEventCreator as event } from './ga';
import { LINK_SELECTED, EXPANDED, COLLAPSED, CLOSE_BUTTON_SELECTED } from 'config/constants/gaCommonActions';

export type CreditCardExpiredModalActionType = 'Update payment method' | 'Contact us';

export const CREDIT_CARD_EXPIRED_ACTION_TYPE: Record<string, CreditCardExpiredModalActionType> = {
  UPDATE_PAYMENT_METHOD: 'Update payment method',
  CONTACT_US: 'Contact us',
};

export const loginSuccess = event((authMethod: AuthMethod = AuthMethod.SBA) => ({
  category: 'Authentication',
  // Logins via the QFF widget are tracked as "SSO" in GA
  action: `Login Success (${authMethod === AuthMethod.WIDGET ? LEGACY_WIDGET_AUTH_METHOD : authMethod})`,
}));

export const positiveStayRatingSelected = event((propertyId: string | number, bookingRef: string) => ({
  category: 'Stay Rating',
  action: `Positive Selected (propertyId: ${propertyId}, bookingRef: ${bookingRef})`,
}));

export const negativeStayRatingSelected = event((propertyId: string | number, bookingRef: string) => ({
  category: 'Stay Rating',
  action: `Negative Selected (propertyId: ${propertyId}, bookingRef: ${bookingRef})`,
}));

export const liveChatButtonSelected = event((available: boolean) => ({
  category: 'Chat Online Now Button',
  action: available ? 'Available Button Selected' : 'Unavailable Button Selected',
}));

export const liveChatNotificationPermissionGranted = event(() => ({
  category: 'Chat Online Now Button',
  action: 'Allow Notifications Permission Granted',
}));

export const liveChatNotificationPermissionDenied = event(() => ({
  category: 'Chat Online Now Button',
  action: 'Allow Notifications Permission Denied',
}));

export const conditionsApplyLinkSelected = event(() => ({
  category: 'Terms and Conditions Link',
  action: LINK_SELECTED,
}));

export const contactUsLinkSelected = event(() => ({
  category: 'Contact Us Link',
  action: LINK_SELECTED,
}));

export const completeFaqLinkSelected = event(() => ({
  category: 'Complete FAQ Link',
  action: LINK_SELECTED,
}));

export const cancellationPolicyPopUpLinkSelected = event(() => ({
  category: 'Cancellation Policy Pop Up',
  action: LINK_SELECTED,
}));

export const cancellationPolicyPopUpCloseButtonSelected = event(() => ({
  category: 'Cancellation Policy Pop Up',
  action: 'Close Button Selected',
}));

export const bookAgainLink = event(() => ({
  category: 'Book Again Link',
  action: LINK_SELECTED,
}));

export const reviewYourStayLink = event(() => ({
  category: 'Review Your Stay Link',
  action: LINK_SELECTED,
}));

export const updatePaymentMethodLink = event(() => ({
  category: 'Update Payment Method Link',
  action: LINK_SELECTED,
}));

export const printItineraryLink = event(() => ({
  category: 'Print Itinerary Link',
  action: LINK_SELECTED,
}));

export const modalCloseButton = event(() => ({
  category: 'Problem Pop Up',
  action: 'Close Button Selected',
}));

export const faqExpanded = event((faqType: string) => ({
  category: `${faqType} FAQ`,
  action: `Accordion ${EXPANDED}`,
}));

export const faqCollapsed = event((faqType: string) => ({
  category: `${faqType} FAQ`,
  action: `Accordion ${COLLAPSED}`,
}));

export const faqLiveChatSelected = event((faqType: string, available: boolean) => ({
  category: `${faqType} FAQ`,
  action: available ? 'Live Chat Available Button Selected' : 'Live Chat Unavailable Button Selected',
}));

export const faqFeedbackHelpful = event((faqType: string, helpful: boolean) => ({
  category: `${faqType} FAQ`,
  action: `Helpful ${helpful ? 'Yes' : 'No'} Button Selected`,
}));

export const ccvHelpPopUpOpened = event(() => ({
  category: 'CCV Help Pop Up',
  action: LINK_SELECTED,
}));

export const ccvHelpPopUpClosed = event(() => ({
  category: 'CCV Help Pop Up',
  action: CLOSE_BUTTON_SELECTED,
}));

export const expiredCreditCardModalOpened = event((type: CreditCardExpiredModalActionType) => ({
  category: 'Expired Credit Card Modal',
  action: `Modal Opened - ${type}`,
}));

export const expiredCreditCardModalClosed = event((type: CreditCardExpiredModalActionType, actionButtonClicked: boolean) => ({
  category: 'Expired Credit Card Modal',
  action: `Modal Closed - ${type}${actionButtonClicked ? ' (Button Clicked)' : ''}`,
}));

export const expiredCreditCardModalLinkSelected = event(
  (type: 'live chat' | 'email' | 'call customer support' | 'call international customer support') => ({
    category: 'Expired Credit Card Modal',
    action: `${LINK_SELECTED} - ${type}`,
  }),
);

export const invoiceDownloadFailedModalOpened = event(() => ({
  category: 'Invoice Download Failed Modal',
  action: `Modal Opened`,
}));

export const appleWalletPassDownloadFailedModalOpened = event(() => ({
  category: 'Apple Wallet Pass Download Failed Modal',
  action: `Modal Opened`,
}));

export const invoiceDownloadFailedModalClosed = event((actionButtonClicked: boolean) => ({
  category: 'Invoice Download Failed Modal',
  action: `Modal Closed${actionButtonClicked ? ' - (Button Clicked)' : ''}`,
}));

export const appleWalletPassDownloadFailedModalClosed = event((actionButtonClicked: boolean) => ({
  category: 'Apple Wallet Pass Download Failed Modal',
  action: `Modal Closed${actionButtonClicked ? ' - (Button Clicked)' : ''}`,
}));

export const invoiceDownloadFailedModalLinkSelected = event(
  (type: 'live chat' | 'email' | 'call customer support' | 'call international customer support') => ({
    category: 'Invoice Download Failed Modal',
    action: `${LINK_SELECTED} - ${type}`,
  }),
);

export const manageBookingReturnLink = event(() => ({
  category: 'Manage Booking Return Link',
  action: 'Link Selected',
}));

export const scheduledPaymentUpdateCreditCardLinkSelected = event(() => ({
  category: 'Scheduled Payment',
  action: `Update Credit Card ${LINK_SELECTED}`,
}));

export const requireUpdateCreditCardLinkSelected = event(() => ({
  category: 'Adyen Payment Gateway Migration',
  action: `Update Credit Card ${LINK_SELECTED}`,
}));

export const manageQantasInPathFlightBookingLinkSelected = event(() => ({
  category: 'Qantas In Path',
  action: `Manage Flight Booking ${LINK_SELECTED}`,
}));
